import type { FC } from 'react';
import dp9 from './../../../resources/content/dp9.png';
import { UnboundedKnapsackWrapper } from './UnboundedKnapsack.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface UnboundedKnapsackProps {}

const UnboundedKnapsack: FC<UnboundedKnapsackProps> = () => (
 <UnboundedKnapsackWrapper>
    <header className="entry-header">
      <h1 className="entry-title" itemProp="headline">Unbounded Knapsack</h1>			
   </header>
   <br/>
   <br/>
   <div className="entry-content clear" itemProp="text"  style={{height: 'auto !important'}}>
      <p className="text-align-justify">Here, “Unbounded” means unlimited copies. You can take the same item multiple times!</p>
      <ul className="text-align-justify">
        <li>A list of choices or items, each with a weight and value.</li>
        <li>**No capacity or budget limits** ~ that's why it's called 'unbounded'.</li>
        <li>The ability to use the same item multiple times.</li>
      </ul>

      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="400" src={dp9} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>
      
      <ul className="text-align-justify">
        <li className="linear"><strong>Initialize DP Table</strong></li>
        <ul className="text-align-justify">
          <li>We start by creating a table to store maximum values for each capacity. This becomes our foundation for building optimal solutions.</li>
        </ul>
        <li className="linear"><strong>Iterate Through Items</strong></li>
        <ul className="text-align-justify">
          <li>Next, we consider each item's weight and value. This is where we evaluate all possible choices.</li>
        </ul>
        <li className="linear"><strong>Update DP Table</strong></li>
        <ul className="text-align-justify">
          <li>For each capacity, we calculate the maximum value using each item's contribution. Here's the key insight ~ we can reuse items!</li>
        </ul>
        <li className="linear"><strong>Determine Optimal Combination</strong></li>
        <ul className="text-align-justify">
          <li>Finally, we find the combination of items that maximizes our total value.</li>
        </ul>
      </ul>	
      <br/>
      <h4 className="text-align-left">Classic Unbounded Knapsack</h4>
      <ul className="text-align-justify">
        <li>N items, each with a specific weight and value</li>
        <li>A knapsack with maximum capacity W</li>
        <li>The ability to use each item unlimited times</li>
        <li>Our goal is to determine the maximum total value we can achieve without exceeding the knapsack's weight capacity.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'Total Knapsack Capacity w = 5\n'+
            '	~ Item 1: weight = 2kg, value = 3\n'+
            '	~ Item 2: weight = 3kg, value = 4\n'+
            '	\n'+
            'Take item with weight 2:\n'+
            '	Remaining = 3kg\n'+
            '	Value for 2kg → 3\n'+
            '👉 Total	 = dp[3] + 3\n'+
            '\n'+
            'Take item with weight 3:\n'+
            '	Remaining = 2kg\n'+
            '	Value for 3kg → 4\n'+
            '👉 Total	  = dp[2] + 4'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Coin Change</h4>
      <ul className="text-align-justify">
        <li>Minimize the number of coins used</li>
        <li>Make exact change for a given amount</li>
        <li>Handle cases where change cannot be made</li>
        <li>The goal is to find the minimum number of coins needed to make an amount.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'Coins = [1, 3, 4], amount = 6\n'+
            '\n'+
            'Possible choices:\n'+
            '	6 coins → 1 Rs (1+1+1+1+1+1)\n'+
            '	2 coins → 3 Rs (3+3) ✅\n'+
            '	1 coin → 4 Rs,2 coins → 1 Rs (4+1+1)\n'+
            '\n'+
            '👉  Answer is 2'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Coin Change II</h4>
      <ul className="text-align-justify">
        <li>We need to count combinations, not permutations, which means the order doesn't matter.</li>
        <li>Every way to make (j − coin) becomes a new way to make j by adding this coin</li>
        <li>The goal is to compute the number of combinations that make up the amount.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'Coins = [1, 2], Target = 4\n'+
            '\n'+
            'Possible ways:\n'+
            '	4 coins → 1 Rs (1+1+1+1)\n'+
            '	2 coins → 1 Rs, 1 coin → 2 Rs (1+1+2)\n'+
            '	2 coins → 2 Rs (2+2)\n'+
            '\n'+
            '👉 Answer is 3'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Rod Cutting</h4>
      <ul className="text-align-justify">
        <li>For each rod length, try all first cut lengths.</li>
        <li>Take the maximum out of: Profit of first piece,  best profit of remaining rod. </li>
        <li>The goal is to determine the maximum profit you get by cutting it into pieces.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'Rod length = 4, prices = [2, 5, 7, 8]\n'+
            '\n'+
            'Initialize:\n'+
            '	For rod length 0: dp[0] = 0\n'+
            'So profit can be calculated as:\n'+
            '	Cut length 1 → 2\n'+
            '	Cut length 2 → max(2+2, 5) = 5\n'+
            '	Cut length 3 → max(2+5,5+2,7) = 7 \n'+
            '	Cut length 4 → max(2+7, 5+5, 7+2, 8) = 10\n'+
            '\n'+
            '👉 Max profit is 10'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>

   </div>
 </UnboundedKnapsackWrapper>
);

export default UnboundedKnapsack;
